import { useState, FormEvent, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Particles } from '../components/Particles';
import { authApi } from '../lib/api';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setMessage({ text: 'Token de recuperação não encontrado. Por favor, solicite um novo link.', type: 'error' });
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const showMessage = (text: string, type: 'success' | 'error' | 'info') => {
    setMessage({ text, type });
    if (type !== 'success') {
      setTimeout(() => setMessage(null), 6000);
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (value !== newPassword) {
      setPasswordError(true);
    } else {
      setPasswordError(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!token) {
      showMessage('Token de recuperação não encontrado.', 'error');
      return;
    }

    if (!newPassword || !confirmPassword) {
      showMessage('Por favor, preencha todos os campos.', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage('As senhas não coincidem.', 'error');
      setPasswordError(true);
      return;
    }

    if (newPassword.length < 8) {
      showMessage('A senha deve ter no mínimo 8 caracteres.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      await authApi.resetPassword(token, newPassword);

      showMessage('Senha alterada com sucesso! Redirecionando para o login...', 'success');

      // Redirecionar para login após 2 segundos
      setTimeout(() => {
        window.location.href = '/login?message=password_updated';
      }, 2000);
    } catch (error: any) {
      console.error('Erro ao redefinir senha:', error);

      let errorMessage = 'Erro ao redefinir senha. Tente novamente.';

      if (error?.response?.status === 400) {
        errorMessage = error.response?.data?.detail || 'Token inválido ou expirado. Solicite um novo link.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'Token não encontrado ou já foi utilizado. Solicite um novo link.';
      } else if (error?.response?.status === 422) {
        errorMessage = 'A senha não atende aos requisitos de segurança.';
      } else if (!error?.response) {
        errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
      }

      showMessage(errorMessage, 'error');
      setIsLoading(false);
    }
  };

  return (
    <>
      <Particles />

      <div className="auth-container">
        {/* Voltar */}
        <Link to="/login" className="back-link">
          ← Voltar para login
        </Link>

        {/* Logo */}
        <div className="auth-logo">
          <img src="/images/logo/logo.png" alt="NeuroAtleta Mindset" />
          <h1>Redefinir Senha</h1>
          <p>Escolha uma nova senha para sua conta</p>
        </div>

        {/* Mensagens */}
        {message && (
          <div id="message-container">
            <div className={`message ${message.type}`}>{message.text}</div>
          </div>
        )}

        {/* Formulário de Redefinição */}
        {token ? (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="newPassword">Nova Senha</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                placeholder="Mínimo 8 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <span className="error-message">A senha deve ter no mínimo 8 caracteres</span>
            </div>

            <div className={`form-group ${passwordError ? 'error' : ''}`}>
              <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Digite a senha novamente"
                value={confirmPassword}
                onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <span className="error-message">As senhas não coincidem</span>
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner"></span> Redefinindo...
                </>
              ) : (
                'Redefinir Senha'
              )}
            </button>
          </form>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <Link to="/forgot-password" className="btn-primary" style={{ display: 'inline-block' }}>
              Solicitar Novo Link
            </Link>
          </div>
        )}

        {/* Informação adicional */}
        <div className="info-box">
          <strong>🔒 Dica de Segurança:</strong> Use uma senha forte com letras, números e caracteres especiais.
        </div>

        {/* Links */}
        <div className="auth-links">
          <div className="divider">
            <span>ou</span>
          </div>
          <Link to="/register" className="auth-link">
            Não tem uma conta? <strong>Criar conta</strong>
          </Link>
        </div>
      </div>
    </>
  );
}
